import apiClient from './client';
import type {
  GroupMeeting,
  SyncAttendancePayload,
  AttendanceSummaryResponse,
  DropOffResponse,
  MemberHistoryResponse,
} from '@/types/attendance';
import type { PaginatedResponse } from '@/types/api';

export async function listGroupMeetings(
  groupId: string,
  params?: { from_date?: string; to_date?: string; status?: string; page?: number; per_page?: number },
): Promise<PaginatedResponse<GroupMeeting>> {
  const res = await apiClient.get<PaginatedResponse<GroupMeeting>>(
    `/groups/${groupId}/meetings/`,
    { params },
  );
  return res.data;
}

export async function getAttendanceSummary(
  groupId: string,
  limit: number = 8,
): Promise<AttendanceSummaryResponse> {
  const res = await apiClient.get<AttendanceSummaryResponse>(
    `/groups/${groupId}/attendance-summary/`,
    { params: { limit } },
  );
  return res.data;
}

/** One-tap get-or-create for the CTA. FE computes the date from meeting_day + today. */
export async function resolveMeeting(
  groupId: string,
  date: string,
): Promise<GroupMeeting> {
  const res = await apiClient.post<{ data: GroupMeeting }>(
    `/groups/${groupId}/meetings/resolve/`,
    { date },
  );
  return res.data.data;
}

export async function createMeeting(
  groupId: string,
  payload: { meeting_date: string; status?: string; topic?: string | null; notes?: string | null; headcount?: number | null },
): Promise<GroupMeeting> {
  const res = await apiClient.post<{ data: GroupMeeting }>(
    `/groups/${groupId}/meetings/`,
    payload,
  );
  return res.data.data;
}

export async function getMeeting(id: string): Promise<GroupMeeting> {
  const res = await apiClient.get<{ data: GroupMeeting }>(`/meetings/${id}/`);
  return res.data.data;
}

export async function updateMeeting(
  id: string,
  payload: Partial<{ meeting_date: string; status: string; topic: string | null; notes: string | null; headcount: number | null }>,
): Promise<GroupMeeting> {
  const res = await apiClient.put<{ data: GroupMeeting }>(`/meetings/${id}/`, payload);
  return res.data.data;
}

export async function deleteMeeting(id: string): Promise<void> {
  await apiClient.delete(`/meetings/${id}/`);
}

/** The hot path: bulk upsert attendance (shell + roster + visitors in one request). */
export async function syncAttendance(
  meetingId: string,
  payload: SyncAttendancePayload,
): Promise<GroupMeeting> {
  const res = await apiClient.put<{ data: GroupMeeting }>(
    `/meetings/${meetingId}/attendance/`,
    payload,
  );
  return res.data.data;
}

export async function copyPreviousAttendance(
  meetingId: string,
): Promise<{ message: string; source_meeting_id: string; meeting: { data: GroupMeeting } }> {
  const res = await apiClient.post<{ message: string; source_meeting_id: string; meeting: { data: GroupMeeting } }>(
    `/meetings/${meetingId}/copy-previous/`,
  );
  return res.data;
}

export async function addVisitor(
  meetingId: string,
  data: { guest_name: string; guest_phone?: string | null },
): Promise<void> {
  await apiClient.post(`/meetings/${meetingId}/visitors/`, data);
}

export async function getMemberAttendanceHistory(
  memberId: string,
  days: number = 90,
): Promise<MemberHistoryResponse> {
  const res = await apiClient.get<MemberHistoryResponse>(
    `/members/${memberId}/attendance/`,
    { params: { days } },
  );
  return res.data;
}

export async function getDropOffReport(
  params?: { sector_id?: string; group_id?: string; category?: string; threshold?: number; page?: number; per_page?: number },
): Promise<DropOffResponse> {
  const res = await apiClient.get<DropOffResponse>('/reports/drop-off/', { params });
  return res.data;
}
